import { Action, createReducer, on } from '@ngrx/store';
import { AddDeleteRemarkStatus } from '../../../dtos/response/remarks-response/add-delete-remark-status';
import { ReservationCreationResponseStatus } from '../../../dtos/response/reservation-creation-response/reservation-creation-response-status';
import { Passenger } from '../../../dtos/response/reservation-response/passenger';
import { RemarksAddRemoveResponse } from '../../../dtos/response/reservation-response/remarks-add-remove-response';
import { ReservationResponse } from '../../../dtos/response/reservation-response/reservation-response';
import { Segment } from '../../../dtos/response/reservation-response/segment';
import { AirlineCode } from '../../../models/airlines/airline-code';
import { Status } from '../../../models/status';
import { updateFCAASegments } from '../../../utils/fcaa-util';
import { TierStatusHelper } from '../../../utils/tier-status-helper';
import { saveContactInformationFieldsComplete } from '../../contact-information-fields-service/state/contact-information-fields.actions';
import {
  addPassengerLoyalty,
  addRemarkFailed,
  addRemarkSucceeded,
  addReservationMessage,
  cancelReservation,
  cancelReservationComplete,
  cancelReservationReset,
  clearAddRemarksStatus,
  clearCreateReservationMessages,
  clearPassengerSeat,
  clearRemoveRemarksStatus,
  createPlaceHolderReservation,
  createPlaceHolderReservationComplete,
  createReservationWithPriceQuote,
  createReservationWithPriceQuoteComplete,
  detectFCAASegments,
  initializeReservationServiceState,
  initialLoadReservation,
  loadReservation,
  loadReservationComplete,
  loadReservationForInitialBooking,
  loadReservationWithDCSPassengerData,
  loadReservationWithDCSPassengerDataComplete,
  removePassengerLapInfant,
  removePassengerLoyalty,
  removeRemarksFailed,
  removeRemarksSucceeded,
  removeReservation,
  replaceReservationSegmentsAndPassengers,
  revertSeats,
  revertSeatsByPassengerSegment,
  setAssociationRevalError,
  setOriginalClassOfService,
  setPassengerCoupon,
  setPassengerCouponMapCoupons,
  setPassengerCoupons,
  setPassengerKnownTravelerNumber,
  setPassengerLapInfant,
  setPassengerLoyalty,
  setPassengerRedressNumber,
  setPassengerSeat,
  setSegmentActionCode,
  setSelectedInvoluntaryChangeOption,
  setTicketNumbers,
  syncPassengerLoyalty,
  syncSeatByPassenger,
  syncSeats,
} from './reservation-service.actions';
import { initialReservationCancelResponseState, initialReservationServiceState, reservationAdapter } from './reservation-service.state';

const featureReducer = createReducer(
  initialReservationServiceState,
  on(loadReservation, (state, { confirmationCode }) => {
    return {
      ...state,
      reservationLookupCrudStatus: Status.LOADING,
      reservationCancelResponse: initialReservationCancelResponseState,
    };
  }),
  on(loadReservationWithDCSPassengerData, (state, { confirmationCode }) => {
    return {
      ...state,
      reservationWithDCSPassengerDataLookupCrudStatus: Status.LOADING,
      reservationCancelResponse: initialReservationCancelResponseState,
    };
  }),
  on(loadReservationForInitialBooking, (state) => {
    return {
      ...state,
      reservationLookupCrudStatus: Status.LOADING,
      reservationCancelResponse: initialReservationCancelResponseState,
    };
  }),
  on(initialLoadReservation, (state, { confirmationCode }) => {
    return {
      ...state,
      reservationLookupCrudStatus: Status.LOADING,
    };
  }),
  on(removeReservation, (state, { confirmationCode }) => {
    return {
      ...state,
      reservation: reservationAdapter.removeOne(confirmationCode, state.reservation),
    };
  }),
  on(loadReservationComplete, (state, { reservationResponse, background }) => {
    if (!reservationResponse) {
      return {
        ...state,
        reservationLookupCrudStatus: Status.FAILED,
      };
    }
    if (!reservationResponse.reservation) {
      return {
        ...state,
        reservationLookupCrudStatus: Status.FAILED,
        reservation: background ? state.reservation : reservationAdapter.setOne(reservationResponse, state.reservation),
      };
    }

    // Update the passengers' loyalty tier status and segments' highest elite tier status to full Tier Status titles
    const updatedPassengers = reservationResponse?.reservation?.passengers?.map((passenger: Passenger) => {
      if (!passenger.loyalty) {
        return passenger;
      }

      // If the passenger has a loyalty airline code that is not Alaska Airlines, we do not need to map the tier status
      if (passenger.loyalty.airlineCode && passenger.loyalty.airlineCode !== AirlineCode.AS) {
        return {
          ...passenger,
          loyalty: {
            ...passenger.loyalty,
            tierStatus: TierStatusHelper.getTierStatusFromText(passenger.loyalty.tierStatus),
          },
        };
      }

      return {
        ...passenger,
        loyalty: {
          ...passenger.loyalty,
          tierStatus: passenger.loyalty.tierStatus,
        },
      };
    });

    const updatedSegments = reservationResponse?.reservation?.allSegments?.map((segment: Segment) => {
      if (!segment.highestEliteTierStatus) {
        return segment;
      }
      return {
        ...segment,
        highestEliteTierStatus: segment.highestEliteTierStatus,
      };
    });

    const updatedReservation = {
      ...reservationResponse,
      reservation: {
        ...reservationResponse.reservation,
        passengers: updatedPassengers,
        allSegments: updatedSegments,
      },
    };

    return {
      ...state,
      reservationLookupCrudStatus: Status.STABLE,
      reservation: reservationAdapter.setOne(updatedReservation, state.reservation),
    };
  }),
  on(loadReservationWithDCSPassengerDataComplete, (state, { reservationResponse }) => {
    if (!reservationResponse) {
      return {
        ...state,
        reservationWithDCSPassengerDataLookupCrudStatus: Status.FAILED,
      };
    }
    if (!reservationResponse.reservation) {
      return {
        ...state,
        reservationWithDCSPassengerDataLookupCrudStatus: Status.FAILED,
        reservationWithDCSPassengerData: reservationAdapter.setOne(reservationResponse, state.reservationWithDCSPassengerData),
      };
    }

    return {
      ...state,
      reservationWithDCSPassengerDataLookupCrudStatus: Status.STABLE,
      reservationWithDCSPassengerData: reservationAdapter.setOne(reservationResponse, state.reservationWithDCSPassengerData),
    };
  }),
  on(cancelReservation, (state, { confirmationCode }) => {
    return {
      ...state,
      reservationCancelResponse: { status: Status.LOADING, statusCode: null, content: '', confirmationCode },
    };
  }),
  on(cancelReservationComplete, (state, { reservationCancelResponse }) => {
    return {
      ...state,
      reservationCancelResponse,
    };
  }),
  on(cancelReservationReset, (state) => {
    return {
      ...state,
      reservationCancelResponse: initialReservationCancelResponseState,
    };
  }),
  on(addPassengerLoyalty, (state, { confirmationCode, passengerHashId }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => updatePassengerLoyaltyStatus(reservationResponse, passengerHashId, Status.UPDATING),
        },
        state.reservation
      ),
    };
  }),
  on(removePassengerLoyalty, (state, { confirmationCode, passengerHashId }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => updatePassengerLoyaltyStatus(reservationResponse, passengerHashId, Status.DELETING),
        },
        state.reservation
      ),
    };
  }),
  on(setPassengerLoyalty, (state, { confirmationCode, passengerHashId, loyalty }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(reservationResponse));
            newReservationResponse.reservation.passengers.find((passenger) => passenger.hashId === passengerHashId).loyalty = loyalty
              ? {
                  ...loyalty,
                }
              : null;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(syncPassengerLoyalty, (state, { confirmationCode, passengerHashId }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => updatePassengerLoyaltyStatus(reservationResponse, passengerHashId, Status.LOADING),
        },
        state.reservation
      ),
    };
  }),
  on(setPassengerRedressNumber, (state, { confirmationCode, passengerHashId, redressNumber }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(reservationResponse));
            newReservationResponse.reservation.passengers.find(
              (passenger) => passenger.hashId === passengerHashId
            ).secureFlightInfo.redressNumber = redressNumber;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(setPassengerKnownTravelerNumber, (state, { confirmationCode, passengerHashId, knownTraveler }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(reservationResponse));
            newReservationResponse.reservation.passengers.find(
              (passenger) => passenger.hashId === passengerHashId
            ).secureFlightInfo.knownTravelerNumber = knownTraveler;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(replaceReservationSegmentsAndPassengers, (state, { reservation }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: reservation.confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            newReservationResponse.reservation.allSegments = reservation.allSegments;
            newReservationResponse.reservation.passengers = reservation.passengers;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  /**
   * Remove the old placeholder reservation from the store when the process to create a new one is started
   */
  on(createPlaceHolderReservation, (state) => ({ ...state, placeholderReservation: null })),
  /**
   * Store the new placeholder reservation response (success or failure)
   */
  on(createPlaceHolderReservationComplete, (state, { placeholderReservation }) => ({ ...state, placeholderReservation })),
  /**
   * Start the process of creating a reservation with price quote
   */
  on(createReservationWithPriceQuote, (state) => ({
    ...state,
    createReservationWithPriceQuoteResponse: {
      response: null,
      status: ReservationCreationResponseStatus.LOADING,
      error: null,
      mappedMessageKeys: [],
    },
  })),
  /**
   * Store the new placeholder reservation response (success or failure)
   */
  on(createReservationWithPriceQuoteComplete, (state, { reservationWithPriceQuoteResponse }) => ({
    ...state,
    createReservationWithPriceQuoteResponse: reservationWithPriceQuoteResponse,
  })),
  on(setAssociationRevalError, (state, { associationRevalError }) => ({ ...state, associationRevalError })),

  // Store the involuntary change option we chose from drop down (IRROP or SC)
  on(setSelectedInvoluntaryChangeOption, (state, { selectedInvoluntaryChangeOption }) => ({ ...state, selectedInvoluntaryChangeOption })),
  on(setTicketNumbers, (state, { confirmationCode, ticketNumbers }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            newReservationResponse.reservation.ticketNumbers = ticketNumbers;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(setPassengerCoupons, (state, { confirmationCode, passengerHashId, coupons }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            let resPassenger = newReservationResponse.reservation.passengers.find((passenger) => passenger.hashId === passengerHashId);
            if (!resPassenger) {
              resPassenger = getExtraSeatPassenger(passengerHashId, newReservationResponse);
            }
            resPassenger.coupons = coupons;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(setPassengerCouponMapCoupons, (state, { confirmationCode, passengerHashId, coupons }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            const resPassenger = newReservationResponse.reservation.passengers.find((passenger) => passenger.hashId === passengerHashId);
            if (resPassenger) {
              resPassenger.couponMapCoupons = coupons;
            }
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(setPassengerCoupon, (state, { confirmationCode, passengerHashId, coupon, index }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            let resPassenger = newReservationResponse.reservation.passengers.find((passenger) => passenger.hashId === passengerHashId);
            if (!resPassenger) {
              resPassenger = getExtraSeatPassenger(passengerHashId, newReservationResponse);
            }
            if (resPassenger && !resPassenger.coupons) {
              resPassenger.coupons = [];
            }
            resPassenger.coupons[index] = coupon;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(setPassengerSeat, (state, { confirmationCode, passengerHashId, seat, index, segmentHashId }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            const resPassengers = getAllReservationPassengers(newReservationResponse);
            const resPassenger = resPassengers.find((passenger) => passenger.hashId === passengerHashId);
            if (!resPassenger.seats) {
              resPassenger.seats = [];
            }
            seat = { ...seat, segmentHashId };
            resPassenger.seats[index] = seat;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(clearPassengerSeat, (state, { confirmationCode, passengerHashId, index }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            const resPassengers = getAllReservationPassengers(newReservationResponse);
            const resPassenger = resPassengers.find((passenger) => passenger.hashId === passengerHashId);
            if (!resPassenger.seats) {
              resPassenger.seats = [];
            } else {
              resPassenger.seats.splice(index, 1);
            }
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(setSegmentActionCode, (state, { confirmationCode, segmentHashId, actionCode }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            const resSegment = newReservationResponse.reservation.allSegments?.find((segment) => segment.hashId === segmentHashId);
            if (resSegment) {
              resSegment.actionCode = actionCode;
            }
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(syncSeats, (state, { confirmationCode }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            const resPassengers = getAllReservationPassengers(newReservationResponse);
            resPassengers.forEach((pax) => {
              pax.originalSeats = pax.seats;
            });
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(syncSeatByPassenger, (state, { confirmationCode, passengerHashId, savedSeat, isSeatCancel }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            const resPassengers = getAllReservationPassengers(newReservationResponse);
            const passenger = resPassengers.find((pax) => pax.hashId === passengerHashId);
            const originalSeatIdx = passenger.originalSeats?.findIndex((s) => s.segmentHashId === savedSeat.segmentHashId);
            if (originalSeatIdx !== undefined && originalSeatIdx > -1) {
              if (isSeatCancel) {
                passenger.originalSeats.splice(originalSeatIdx, 1);
              } else {
                passenger.originalSeats[originalSeatIdx] = savedSeat;
              }
            } else {
              const newSeat = passenger.seats.find((s) => s.segmentHashId === savedSeat.segmentHashId);
              if (newSeat) {
                passenger.originalSeats.push({ ...newSeat });
              }
            }
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(revertSeats, (state, { confirmationCode }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            const resPassengers = getAllReservationPassengers(newReservationResponse);
            resPassengers.forEach((pax) => {
              pax.seats = pax.originalSeats;
            });
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(revertSeatsByPassengerSegment, (state, { confirmationCode, segmentHashId, passengerHashId }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            const resPassengers = getAllReservationPassengers(newReservationResponse);
            const passenger = resPassengers.find((pax) => pax.hashId === passengerHashId);
            const originalSeat = passenger?.originalSeats?.find((s) => s.segmentHashId === segmentHashId);
            if (originalSeat) {
              const seatIdx = passenger.seats?.findIndex((s) => s.segmentHashId === segmentHashId);
              if (seatIdx !== undefined && seatIdx > -1) {
                passenger.seats[seatIdx] = originalSeat;
              } else {
                passenger.seats.push({ ...originalSeat });
              }
            }
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(setOriginalClassOfService, (state, { confirmationCode, coupons }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (originalReservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(originalReservationResponse));
            const updatedSegments: Segment[] = [];
            const firstClassServiceCodes = ['U', 'J', 'C', 'D', 'I'];

            newReservationResponse.reservation.allSegments.forEach((segment) => {
              if (firstClassServiceCodes.includes(segment.serviceClassCode)) {
                updatedSegments.push({
                  ...segment,
                  originalClassOfService: coupons?.filter(
                    (coupon) =>
                      (coupon.status === 'OK' || coupon.status === 'CKIN') &&
                      coupon.airlineCode === segment.marketedByAirlineCode &&
                      coupon.departureAirport === segment.departureAirport &&
                      coupon.arrivalAirport === segment.arrivalAirport &&
                      coupon.flightNumber === segment.operatingAirlineFlightNumber
                  )[0]?.classOfService,
                });
              } else {
                updatedSegments.push(segment);
              }
            });
            newReservationResponse.reservation.allSegments = updatedSegments;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  /**
   * Completes the savePassenger request to the API to remove the International Documents
   */
  on(setPassengerLapInfant, (state, { confirmationCode, passengerHashId, lapInfant }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(reservationResponse));
            newReservationResponse.reservation.passengers.find((passenger) => passenger.hashId === passengerHashId).lapInfant = lapInfant;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  /**
   * Completes the savePassenger request to the API to remove the International Documents
   */
  on(removePassengerLapInfant, (state, { confirmationCode, passengerHashId }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(reservationResponse));
            newReservationResponse.reservation.passengers.find((passenger) => passenger.hashId === passengerHashId).lapInfant = null;
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(detectFCAASegments, (state, { confirmationCode, coupons }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (resResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(resResponse));
            updateFCAASegments(coupons, newReservationResponse.reservation);
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),

  on(initializeReservationServiceState, (state) => ({ ...state, ...initialReservationServiceState })),

  on(removeRemarksSucceeded, (state, { confirmationCode, remarkIds, remarksRemoveResponse }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => removeRemarks(remarkIds, reservationResponse),
        },
        state.reservation
      ),
      deleteRemarkStatus: remarksRemoveResponse.status,
    };
  }),

  on(removeRemarksFailed, (state, { status }) => {
    return {
      ...state,
      deleteRemarkStatus: status,
    };
  }),

  on(clearRemoveRemarksStatus, (state) => {
    return {
      ...state,
      deleteRemarkStatus: AddDeleteRemarkStatus.NONE,
    };
  }),

  on(clearAddRemarksStatus, (state) => {
    return {
      ...state,
      addRemarkStatus: AddDeleteRemarkStatus.NONE,
    };
  }),

  on(addRemarkSucceeded, (state, { confirmationCode, remarksAddRemoveResponse }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => addRemarks(remarksAddRemoveResponse, reservationResponse),
        },
        state.reservation
      ),
      addRemarkStatus: AddDeleteRemarkStatus.SUCCESS,
    };
  }),

  on(addRemarkFailed, (state, { status, errorMessage }) => {
    return {
      ...state,
      addRemarkStatus: status,
    };
  }),
  on(addReservationMessage, (state, { confirmationCode, messageKey }) => {
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: confirmationCode,
          map: (reservationResponse) => {
            const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(reservationResponse));
            if (!newReservationResponse.reservation.mappedMessageKeys) {
              newReservationResponse.reservation.mappedMessageKeys = [messageKey];
            } else {
              newReservationResponse.reservation.mappedMessageKeys.push(messageKey);
            }
            return newReservationResponse;
          },
        },
        state.reservation
      ),
    };
  }),
  on(clearCreateReservationMessages, (state) => ({
    ...state,
    createReservationWithPriceQuoteResponse: {
      ...state.createReservationWithPriceQuoteResponse,
      mappedMessageKeys: [],
    },
  })),
  on(saveContactInformationFieldsComplete, (state, { response }) => {
    const updatedReservation = response?.updatedReservation;
    if (!updatedReservation?.confirmationCode || !updatedReservation?.billingDetails) {
      return state;
    }
    return {
      ...state,
      reservation: reservationAdapter.mapOne(
        {
          id: updatedReservation.confirmationCode,
          map: (reservationResponse) => ({
            ...reservationResponse,
            reservation: {
              ...reservationResponse.reservation,
              billingDetails: updatedReservation.billingDetails,
            },
          }),
        },
        state.reservation
      ),
    };
  })
);

export function reservationServiceReducer(state = initialReservationServiceState, action: Action) {
  return featureReducer(state, action);
}

function removeRemarks(remarkIds: number[], reservationResponse: ReservationResponse): ReservationResponse {
  const ids = remarkIds.map((id) => id.toString());
  const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(reservationResponse));
  const remarks = newReservationResponse.reservation.remarks.filter((remark) => !ids.includes(remark.id));

  newReservationResponse.reservation.remarks = remarks;
  return newReservationResponse;
}

function addRemarks(remarksAddRemoveResponse: RemarksAddRemoveResponse, reservationResponse): ReservationResponse {
  const ids = remarksAddRemoveResponse.remarkIds.map((id) => id.toString());
  const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(reservationResponse));
  for (let i = 0; i < remarksAddRemoveResponse.remarks.length; i++) {
    newReservationResponse.reservation.remarks.push({
      id: ids[i],
      type: 'HS',
      remarkLines: [remarksAddRemoveResponse.remarks[i].toUpperCase()],
      isReadOnly: false,
    });
  }

  return newReservationResponse;
}

function updatePassengerLoyaltyStatus(
  reservationResponse: ReservationResponse,
  passengerHashId: string,
  status: Status
): ReservationResponse {
  const newReservationResponse: ReservationResponse = JSON.parse(JSON.stringify(reservationResponse));
  const responsePassenger = newReservationResponse.reservation.passengers.find((passenger) => passenger.hashId === passengerHashId);
  if (!responsePassenger.loyalty) {
    responsePassenger.loyalty = { id: null, number: null, airlineCode: null, tierStatus: null, status };
  } else {
    responsePassenger.loyalty.status = status;
  }
  return newReservationResponse;
}

function getAllReservationPassengers(reservationResponse: ReservationResponse): Passenger[] {
  const extraSeatPassengers = reservationResponse.reservation.passengers
    .map((passenger) => passenger.extraSeatRefs)
    .filter((passenger) => passenger)
    .flat();
  return reservationResponse.reservation.passengers.concat(extraSeatPassengers);
}

function getExtraSeatPassenger(passengerHashId, reservationResponse: ReservationResponse) {
  const allPassengers = getAllReservationPassengers(reservationResponse);
  return allPassengers.find((pax) => pax.hashId === passengerHashId);
}
